import { APIGroup } from '@spona/rest-api-types';
import { proxy } from 'valtio';
import { settingsApi } from '@/API/settingsAPI.ts';
import { userApi } from '@/API/userAPI.ts';

/**
 * Global application settings and defaults
 */
export interface AppSettings {
  orderDefaults: {
    defaultDeadlineEnd: string;
  };
  isExternalSeller: boolean;
  userRole?: string;
  isInitialized: boolean;
  currentUserGroups: APIGroup[]; // Array of group names or IDs
}

export const AppSettingsState = proxy<AppSettings>({
  orderDefaults: {
    defaultDeadlineEnd: '50'
  },
  isExternalSeller: false,
  userRole: undefined,
  isInitialized: false,
  currentUserGroups: [],
});

/**
 * Actions for managing app settings
 */
export const AppSettingsActions = {
  /**
   * Load order defaults from the server
   */
  loadOrderDefaults: async () => {
    try {
      console.debug('AppSettings: Loading order defaults from server');
      const result = await settingsApi.getDefaultDeadline();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch default deadline');
      }
      
      AppSettingsState.orderDefaults = {
        defaultDeadlineEnd: result.data.to?.toString() || '50'
      };
      
      console.debug('AppSettings: Order defaults loaded', AppSettingsState.orderDefaults);
    } catch (error) {
      console.error('AppSettings: Failed to load order defaults', error);
      // Keep default values on error
    }
  },

  loadUserGroups: async () => {
    try {
      console.debug('AppSettings: Loading user groups from server');
      const result = await userApi.getAllGroups();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch user groups');
      }
      
      AppSettingsState.currentUserGroups = result.data as APIGroup[];
      console.debug('AppSettings: User groups loaded', AppSettingsState.currentUserGroups);
    } catch (error) {
      console.error('AppSettings: Failed to load user groups', error);
      // Keep current groups empty on error
    }
  },

  /**
   * Set user role information
   */
  setUserRole: (role: string) => {
    AppSettingsState.userRole = role;
    AppSettingsState.isExternalSeller = role === 'external_seller';
    console.debug('AppSettings: User role set', { role, isExternalSeller: AppSettingsState.isExternalSeller });
  },

  /**
   * Initialize all app settings
   */
  initialize: async () => {
    if (AppSettingsState.isInitialized) {
      console.debug('AppSettings: Already initialized, skipping');
      return;
    }

    console.debug('AppSettings: Initializing app settings');
    
    try {
      // Load order defaults
      await AppSettingsActions.loadOrderDefaults();
      await AppSettingsActions.loadUserGroups();
      
      // You could load other settings here like user info, etc.
      // await AppSettingsActions.loadUserInfo();
      
      AppSettingsState.isInitialized = true;
      console.debug('AppSettings: Initialization complete');
    } catch (error) {
      console.error('AppSettings: Initialization failed', error);
    }
  }
};